import { getNextSnapshot, setup } from 'xstate'

/**
 * The exhibitor application lifecycle — eight states, eight guarded transitions.
 *
 * ```
 * draft ──submit──> submitted ──verify──> pending_verification ──approve──> approved
 *                                               │                             │
 *                                               └──reject──> rejected         │ create-invoice
 *                                                                             ▼
 *                                     payment_completed <──activate── payment_pending
 * ```
 *
 * Two things here are load-bearing, and both are invariants that shipped broken in v1.
 *
 * **Invariant 01 — `canceled` is reachable from every state except `payment_completed`.**
 * Canceling a paid application would call `releaseAllBooths()` on *confirmed* booths while
 * leaving a paid invoice orphaned: the exhibitor has paid for floor space that the floor map
 * then shows as available, and somebody else sells it. The edge is absent from the machine
 * AND refused inside `cancel()` (convention C1), because a machine the service forgets to
 * ask is a machine that guards nothing.
 *
 * **`payment_completed` is terminal.** `markPaidNoInvoice()` is described in the flow
 * document as one-way with no reverse transition, and so is settlement. A refund is a credit
 * note against the invoice, not a walk backwards through this machine — walking backwards
 * would have to un-confirm booths that the exhibitor is already building on.
 *
 * Like every machine in this package it is shared with the frontends (stack §10). The API is
 * authoritative; the admin console runs the same definition to decide which buttons exist, so
 * "Cancel" is absent on a paid application rather than present and answered with a 409.
 */

export const EXHIBITOR_STATES = [
  'draft',
  'submitted',
  'pending_verification',
  'approved',
  'payment_pending',
  'payment_completed',
  'rejected',
  'canceled',
] as const
export type ExhibitorState = (typeof EXHIBITOR_STATES)[number]

export const EXHIBITOR_EVENTS = [
  'SUBMIT',
  'VERIFY',
  'APPROVE',
  'REJECT',
  'CREATE_INVOICE',
  'ACTIVATE',
  'MARK_PAID_NO_INVOICE',
  'CANCEL',
] as const

export type ExhibitorEvent =
  | { type: 'SUBMIT' }
  | { type: 'VERIFY' }
  | { type: 'APPROVE' }
  | { type: 'REJECT' }
  | { type: 'CREATE_INVOICE' }
  | { type: 'ACTIVATE' }
  /** The out-of-band cohort path. The service additionally refuses a linked invoice. */
  | { type: 'MARK_PAID_NO_INVOICE' }
  | { type: 'CANCEL' }

/**
 * Spread into every state that may be abandoned — and deliberately **not** into
 * `payment_completed` (invariant 01) or `canceled`.
 *
 * Written as a shared fragment rather than repeated so that the states which lack it read as
 * an exception. A reader scanning the machine below sees `...CANCELABLE` six times and its
 * absence twice, which is the shape of the rule.
 */
const CANCELABLE = { CANCEL: 'canceled' } as const

export const exhibitorApplicationMachine = setup({
  // XState's documented way to declare the event union: a type annotation in an expression's
  // slot. Nothing is cast at runtime.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: { events: {} as ExhibitorEvent },
}).createMachine({
  id: 'exhibitorApplication',
  initial: 'draft',
  states: {
    draft: { on: { SUBMIT: 'submitted', ...CANCELABLE } },
    submitted: { on: { VERIFY: 'pending_verification', ...CANCELABLE } },
    pending_verification: { on: { APPROVE: 'approved', REJECT: 'rejected', ...CANCELABLE } },

    /**
     * The fork: the invoiced path and the out-of-band one.
     *
     * `MARK_PAID_NO_INVOICE` jumps straight to `payment_completed` because for those cohorts
     * there is no invoice to settle. The service refuses it when `invoice_id` is set — that
     * is a fact about the row, not about the state, so it cannot live here.
     */
    approved: {
      on: {
        CREATE_INVOICE: 'payment_pending',
        MARK_PAID_NO_INVOICE: 'payment_completed',
        ...CANCELABLE,
      },
    },

    payment_pending: { on: { ACTIVATE: 'payment_completed', ...CANCELABLE } },

    /** Invariant 01. No `CANCEL`, and no way back. */
    payment_completed: { type: 'final' },

    /** Rejected applicants hold no booths — `reject()` released them — but may still withdraw. */
    rejected: { on: { ...CANCELABLE } },

    canceled: { type: 'final' },
  },
})

/**
 * Does this state have an outgoing edge for this event *at all*?
 *
 * Asked of the state node rather than inferred from "did the value change", because on a
 * self-directed question the two answers differ: `applyExhibitorEvent('canceled', CANCEL)`
 * returns `canceled` whether the edge exists or not. The flow document says `cancel()`
 * refuses an application that is *already* canceled, so that difference is the rule, not a
 * technicality — inferring from the value would have made re-canceling silently succeed.
 */
function accepts(state: ExhibitorState, type: ExhibitorEvent['type']): boolean {
  // `states` is typed as a partial record, because XState cannot know the key came from the
  // machine's own state list. `EXHIBITOR_STATES` and the machine are written together, and
  // the "eight states" test asserts they match — so this is checked rather than assumed.
  const node = exhibitorApplicationMachine.states[state]
  return node !== undefined && Object.hasOwn(node.on, type)
}

/**
 * Where does `state` go on `event`? Returns `state` unchanged when the edge does not exist.
 *
 * Refusal is "nothing moved", which is what the service turns into a `conflict` naming the
 * event and the state it was actually in. `getNextSnapshot` rather than `machine.transition`:
 * the latter wants an actor scope it cannot have outside a running actor and throws deep
 * inside XState rather than returning something a caller can read.
 */
export function applyExhibitorEvent(state: ExhibitorState, event: ExhibitorEvent): ExhibitorState {
  const start = exhibitorApplicationMachine.resolveState({ value: state, context: {} })
  const next = getNextSnapshot(exhibitorApplicationMachine, start, event).value
  // XState types a snapshot's value as `StateValue`, which admits nested objects a flat
  // machine cannot produce. Narrowed rather than asserted: an unexpected shape means the
  // state did not move, which is the same answer as a missing edge.
  return typeof next === 'string' ? next : state
}

/**
 * **Invariant 01, as the question a UI asks.**
 *
 * Derived from the machine rather than restated as `state !== 'payment_completed'`, so the
 * console's button and the service's guard cannot drift: there is one definition of which
 * applications may be withdrawn, and both read it. False for `canceled` as well as for
 * `payment_completed` — withdrawing a withdrawn application is a no-op the operator should be
 * told about rather than one they should be allowed to repeat.
 */
export function canCancelExhibitor(state: ExhibitorState): boolean {
  return accepts(state, 'CANCEL')
}
